/**
 * Version History Panel
 * Shows version history, current JSON config (always expanded & editable), and allows activating old versions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { agentApi } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loading } from '@/components/common';
import { useI18n } from '@/i18n';
import { History, RotateCcw, Eye, EyeOff, Copy, Check, Download, Code, Save, AlertCircle } from 'lucide-react';
import type { AgentConfigVersion, AgentConfiguration } from '@/types';

interface VersionHistoryProps {
  agentType: string;
  currentVersion: number;
  currentConfig?: Partial<AgentConfiguration>;
  onConfigReplace?: (newConfig: Partial<AgentConfiguration>) => void;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({
  agentType,
  currentVersion: _currentVersion,
  currentConfig,
  onConfigReplace,
}) => {
  const queryClient = useQueryClient();
  const { messages, language } = useI18n();
  const t = messages.agentConfig.versionHistory;
  const tj = messages.agentConfig.jsonPreview;
  const [viewingVersion, setViewingVersion] = useState<AgentConfigVersion | null>(null);
  const [copied, setCopied] = useState(false);

  // Editable JSON state
  const [editedJson, setEditedJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [hasJsonChanges, setHasJsonChanges] = useState(false);

  // Initialize edited JSON when currentConfig changes
  useEffect(() => {
    if (currentConfig) {
      const jsonStr = JSON.stringify(currentConfig, null, 2);
      setEditedJson(jsonStr);
      setHasJsonChanges(false);
      setJsonError(null);
    }
  }, [currentConfig]);

  // Handle JSON editor changes
  const handleJsonChange = useCallback((value: string | undefined) => {
    if (value === undefined) return;
    setEditedJson(value);

    // Validate JSON
    try {
      JSON.parse(value);
      setJsonError(null);
      // Check if different from original
      const originalJson = JSON.stringify(currentConfig, null, 2);
      setHasJsonChanges(value !== originalJson);
    } catch (e) {
      setJsonError((e as Error).message);
      setHasJsonChanges(false);
    }
  }, [currentConfig]);

  // Apply JSON changes
  const handleApplyJsonChanges = useCallback(() => {
    if (jsonError || !hasJsonChanges) return;

    try {
      const parsed = JSON.parse(editedJson);
      if (onConfigReplace) {
        onConfigReplace(parsed);
        setHasJsonChanges(false);
      }
    } catch (e) {
      setJsonError((e as Error).message);
    }
  }, [editedJson, jsonError, hasJsonChanges, onConfigReplace]);

  // Reset JSON to original
  const handleResetJson = useCallback(() => {
    if (currentConfig) {
      const jsonStr = JSON.stringify(currentConfig, null, 2);
      setEditedJson(jsonStr);
      setHasJsonChanges(false);
      setJsonError(null);
    }
  }, [currentConfig]);

  // Fetch versions
  const { data: versions, isLoading } = useQuery({
    queryKey: ['agent-versions', agentType],
    queryFn: () => agentApi.getVersions(agentType),
  });

  // Activate version mutation
  const activateMutation = useMutation({
    mutationFn: ({ version, reason }: { version: number; reason?: string }) =>
      agentApi.activateVersion(agentType, version, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-config', agentType] });
      queryClient.invalidateQueries({ queryKey: ['agent-versions', agentType] });
      queryClient.invalidateQueries({ queryKey: ['agents-list'] });
    },
  });

  const handleActivate = (version: number) => {
    if (confirm(t.confirmRestore.replace('{version}', String(version)))) {
      activateMutation.mutate({ version, reason: t.restoreReason });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US');
  };

  // JSON copy/download helpers
  const handleCopy = async (config: any) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(config, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = (config: any, version?: number) => {
    const jsonString = JSON.stringify(config, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = version
      ? `${agentType}-v${version}-config.json`
      : `${agentType}-config.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <Loading message={t.loading} />;
  }

  return (
    <div className="space-y-4">
      {/* Current Configuration JSON Preview - Always Expanded & Editable */}
      {currentConfig && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Code className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-medium text-gray-900">{tj?.title || 'JSON配置'}</h3>
              <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                {t.editing || '编辑中'}
              </span>
              {hasJsonChanges && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                  {language === 'zh' ? '有修改' : 'Modified'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasJsonChanges && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetJson}
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    {language === 'zh' ? '重置' : 'Reset'}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleApplyJsonChanges}
                    disabled={!!jsonError}
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {language === 'zh' ? '应用更改' : 'Apply Changes'}
                  </Button>
                </>
              )}
              <Button variant="secondary" size="sm" onClick={() => handleCopy(currentConfig)}>
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? tj?.copied || '已复制' : tj?.copy || '复制'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleDownload(currentConfig)}>
                <Download className="w-4 h-4 mr-1" />
                {tj?.download || '下载'}
              </Button>
            </div>
          </div>

          {/* JSON Error Display */}
          {jsonError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="font-mono text-xs">{jsonError}</span>
            </div>
          )}

          {/* Monaco Editor - Always visible */}
          <div className="h-[400px] border border-gray-300 rounded-lg overflow-hidden">
            <Editor
              height="100%"
              defaultLanguage="json"
              value={editedJson}
              onChange={handleJsonChange}
              options={{
                minimap: { enabled: false },
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                fontSize: 13,
                tabSize: 2,
                wordWrap: 'on',
                automaticLayout: true,
              }}
              theme="vs-dark"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {language === 'zh'
              ? '直接编辑 JSON 并点击"应用更改"来更新配置'
              : 'Edit JSON directly and click "Apply Changes" to update configuration'}
          </p>
        </Card>
      )}

      {/* Version History */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">{t.title}</h3>
        </div>

        {versions && versions.length > 0 ? (
          <div className="space-y-2">
            {versions.map((version) => (
              <div
                key={version.version}
                className={`p-3 rounded-lg border ${
                  version.is_active
                    ? 'border-emerald-300 bg-emerald-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">
                      v{version.version}
                    </span>
                    {version.is_active && (
                      <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                        {messages.agentConfig.active}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewingVersion(viewingVersion?.version === version.version ? null : version)}
                      title={t.viewConfig || '查看配置'}
                    >
                      {viewingVersion?.version === version.version ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </Button>
                    {!version.is_active && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleActivate(version.version)}
                        disabled={activateMutation.isPending}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        {t.restore}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-sm text-gray-500">
                  <span>{formatDate(version.created_at)}</span>
                  {version.description && (
                    <span className="ml-2">- {version.description}</span>
                  )}
                </div>
                {version.created_by && (
                  <div className="mt-1 text-xs text-gray-400">
                    {t.createdBy}: {version.created_by}
                  </div>
                )}

                {/* Inline JSON Preview for this version */}
                {viewingVersion?.version === version.version && version.configuration && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {t.versionConfig?.replace('{version}', String(version.version)) || `v${version.version} 配置`}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(version.configuration)}
                        >
                          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(version.configuration, version.version)}
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                        {/* Apply this version's config to editor */}
                        {onConfigReplace && !version.is_active && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              if (version.configuration) {
                                setEditedJson(JSON.stringify(version.configuration, null, 2));
                                setHasJsonChanges(true);
                                setJsonError(null);
                              }
                            }}
                          >
                            {language === 'zh' ? '载入到编辑器' : 'Load to Editor'}
                          </Button>
                        )}
                      </div>
                    </div>
                    <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-[300px] text-xs font-mono">
                      <code>{JSON.stringify(version.configuration, null, 2)}</code>
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">{t.noHistory}</p>
        )}
      </Card>
    </div>
  );
};

export default VersionHistory;
